// CC-178: single display spec for the run screen; payroll_pdf.py mirrors it 1:1
// (enforced by backend/tests/test_cc178_pdf_screen_parity.py, which runs this file in node).

const _fmt2 = (n) => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const money = (v) => (v == null ? "—" : `$${_fmt2(v)}`);
export const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);
const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;

export const showTfs = (rows, key) => (rows || []).some((r) => r[key] !== undefined);

export const tierLabel = (p) => `${pct(p.tier_pct)}${p.team_leader ? " · TL" : ""}`;
export const techSubLabel = (p) => `${p.license_level || "—"} · ${p.pay_type || "—"}${p.team_leader ? " · TL" : ""}`;
export const paylineBeforeCsr = (p) => r2((p.final_commission || 0) + (p.csr_slice_deducted || 0));
export const jobBeforeCsr = (j) => r2((j.job_attributed_commission || 0) + (j.csr_slice_deducted || 0));

export function paylineMoneyCells(p, withTfs, withMinWage = false) {
  return [
    ["Revenue", money(p.weekly_revenue)],
    ...(withTfs ? [["TFS", money(p.weekly_tfs)]] : []),
    ["CC Fee", money(p.cc_fee_dollars)],
    ["Permits", money(p.weekly_permits)],
    ["Surges", money(p.weekly_surges)],
    ["Materials", money(p.weekly_materials)],
    ["Commissionable", money(p.commissionable_revenue)],
    ["Commission before CSR", money(paylineBeforeCsr(p))],
    ["Commission", money(p.final_commission)],
    ...(withMinWage ? [["Min wage supplement", money(p.min_wage_supplement ?? 0)]] : []),
  ];
}

// CC-156: minimum-wage supplement rows (screen, PDF and tech review page share these cells).
export const showMinWage = (rows) => (rows || []).some((p) => p.min_wage_supplement !== undefined);
export const MIN_WAGE_HDR = ["Tech", "Paid hours (Working + Driving + Meeting)", "Excluded hours", "MO rate", "Floor", "Week pay", "Supplement"];
export const minWageCells = (r) => [
  r.name || "—", Number(r.paid_hours || 0).toFixed(2), Number(r.excluded_hours || 0).toFixed(2), money(r.rate),
  money(r.floor), money(r.week_pay), money(r.supplement),
];

// CC-156 balance: opening balance, projected recovery (booked on approval), remaining.
export const MIN_WAGE_BAL_HDR = ["Tech", "Opening balance", "Recovered this week", "Supplement this week", "Remaining balance"];
export const minWageBalanceCells = (b) => [
  b.name || "—", money(b.opening), money(b.recovery ? -Number(b.recovery) : 0), money(b.supplement), money(b.remaining),
];

export function jobLineMoneyCells(j, withTfs) {
  return [
    ["Invoice", money(j.invoice_total)],
    ["Materials", money(j.materials)],
    ["Permits", money(j.permits)],
    ["Surges", money(j.surges)],
    ...(withTfs ? [["TFS", money(j.job_tfs)]] : []),
    ["CC", money(j.job_cc_fee)],
    ["Commissionable", money(j.job_commissionable_revenue)],
    ["Commission before CSR", money(jobBeforeCsr(j))],
    ["Attributed $", money(j.job_attributed_commission)],
  ];
}

// CC-268 (display only): for a tech with material overage, each job row shows commissionable × rate (+NTF) (−CSR)
// and the weekly overage becomes its own line that takes the rounding cents, so rows + line = final_commission exactly.
export const OVERAGE_LABEL = "Material overage deduction";
export function overageView(p) {
  const jobs = p.job_lines || [];
  const ov = Number(p.material_overage_deduction || 0);
  if (!(ov > 0) || !jobs.length) return { jobs, line: null };
  const rate = Number(p.tier_pct || 0);
  const rows = jobs.map((j) => {
    const before = r2(r2(Number(j.job_commissionable_revenue || 0) * rate) + Number(j.job_ntf_dollars || 0));
    return { ...j, job_attributed_commission: r2(before - Number(j.csr_slice_deducted || 0)) };
  });
  const sum = r2(rows.reduce((s, j) => s + j.job_attributed_commission, 0));
  const exact = -r2(ov * rate);
  const gap = r2(Number(p.final_commission || 0) - sum - exact);
  // Rounding alone moves the line by at most a cent per job; anything bigger means the saved job rows don't add up.
  const mismatch = Math.abs(gap) > 0.01 * (jobs.length + 1) ? gap : null;
  const pooled = p.pooled_materials != null;  // held jobs released this run are pooled into the 10% test
  const comm = Number((pooled ? p.pooled_commissionable : p.commissionable_revenue) || 0);
  const allowancePct = p.material_allowance_pct != null ? Number(p.material_allowance_pct)
    : Math.round((Number(p.materials_ratio || 0) - Number(p.material_overage_pct || 0)) * 10000) / 10000;
  return {
    jobs: rows,
    line: {
      materials: Number((pooled ? p.pooled_materials : p.weekly_materials) || 0), commissionable: comm, allowance_pct: allowancePct,
      allowance: r2(comm * allowancePct), overage: pooled ? Number(p.pooled_overage_deduction || 0) : ov, rate,
      week_share: pooled ? ov : null,
      commission_removed: mismatch == null ? r2(Number(p.final_commission || 0) - sum) : exact,
      mismatch,
    },
  };
}
export const overageLineText = (l) => (l.week_share != null
  ? `Pooled materials ${money(l.materials)} − ${pct(l.allowance_pct)} allowance on ${money(l.commissionable)} commissionable `
    + `(${money(l.allowance)}) = overage ${money(l.overage)}; this week's jobs carry ${money(l.week_share)} × ${pct(l.rate)} `
    + `= commission ${money(l.commission_removed)}`
  : `Weekly materials ${money(l.materials)} − ${pct(l.allowance_pct)} allowance on ${money(l.commissionable)} commissionable `
    + `(${money(l.allowance)}) = overage ${money(l.overage)} × ${pct(l.rate)} = commission ${money(l.commission_removed)}`);

// Aggregate 10% material test, one line per tech (screen + PDF + team review share this text).
export function materialsLine(p) {
  const pooled = p.pooled_materials != null;
  const ratio = Number((pooled ? p.pooled_materials_ratio : p.materials_ratio) || 0);
  let s = `Materials ${pct(ratio)} of commissionable revenue`;
  if (p.material_allowance_pct != null) s += ` (threshold ${pct(p.material_allowance_pct)})`;
  if (pooled) {
    const n = (p.carried_lines || []).filter((c) => !c.flag).length;
    s += ` · includes ${n} held job${n === 1 ? "" : "s"} released this run`;
  }
  return s;
}

export const overageMismatchText = (l) =>
  `Job rows do not add up to this tech's weekly totals — off by ${money(l.mismatch)}. Review before relying on this printout.`;

export function formulaLine(p) {
  const ded = [
    ["CC fees", p.cc_fee_dollars],
    ["Permits", p.weekly_permits],
    ["Surges", p.weekly_surges],
    ...(p.weekly_tfs !== undefined ? [["TFS", p.weekly_tfs]] : []),
  ].filter(([, v]) => Number(v || 0) > 0);
  let s = `Revenue ${money(p.weekly_revenue)}`;
  ded.forEach(([k, v]) => { s += ` − ${k} ${money(v)}`; });
  s += ` = Pool ${money(p.commissionable_revenue)}`;
  if (Number(p.material_overage_deduction || 0) > 0) {
    s += ` − Material overage ${money(p.material_overage_deduction)} = ${money(p.net_after_materials)}`;
  }
  s += ` × ${tierLabel(p)} = ${money(p.commission_gross)}`;
  if (Number(p.ntf_dollars || 0) > 0) s += ` + NTF ${money(p.ntf_dollars)}`;
  if (Number(p.csr_slice_deducted || 0) > 0) s += ` − CSR ${money(p.csr_slice_deducted)}`;
  s += ` = Commission ${money(p.final_commission)}`;
  return s;
}

// CC-179: CSR card rows (screen, PDF and CSR review page share these cells).
export const CSR_JOB_HDR = ["Job #", "Customer", "Primary tech", "Job revenue", "SP tier", "SP pool", "CSR %", "CSR $"];
export const csrJobCells = (j) => [
  String(j.job_number ?? ""), j.customer || "—", j.primary_tech || "—", money(j.job_revenue),
  pct(j.sp_tier_pct), money(j.sp_commission_pool), pct(j.csr_pct), money(j.csr_dollars),
];
export const CSR_NOT_COUNTED_HDR = ["Job #", "Customer", "CSR", "Sale date", "Why it did not count"];
export const csrNotCountedCells = (s) => [String(s.job_number ?? ""), s.customer || "—", s.csr_name || "—", s.sale_date || "—", s.reason];
